import { z } from 'zod/v4';

/**
 * Animation type. Mirrors `EAnimationType` from
 * `libs/components/sp-animation-settings/type/animation-settings.type.ts`.
 * The `'running_stripe'` literal uses the underscored form (host-app value).
 */
export const animationTypeSchemaLegacy = z.enum(['fade', 'slide', 'scale', 'running_stripe']);

/**
 * Animation direction. Mirrors `EAnimationPosition`.
 */
export const animationPositionSchemaLegacy = z.enum(['left', 'top', 'right', 'bottom']);

/**
 * Animation speed bucket. Mirrors `EAnimationSpeed`.
 */
export const animationSpeedSchemaLegacy = z.enum(['slow', 'medium', 'fast']);

/**
 * Animation settings shared by zero-block (and animatable default) widgets —
 * matches the FormGroup produced by `animationSettingsFormGroup()` in
 * `libs/components/sp-animation-settings/form/animation-settings.form.ts`.
 */
export const animationSchemaLegacy = z.object({
    isActive: z.boolean(),
    animationType: animationTypeSchemaLegacy,
    animationPosition: animationPositionSchemaLegacy,
    animationSpeed: animationSpeedSchemaLegacy,
});

export type AnimationSettingsLegacy = z.infer<typeof animationSchemaLegacy>;

/**
 * Shape fragment for widget data schemas that carry the animation group —
 * spread into the data `z.object`. Form twin: `animationPlugin` in
 * `@sites/entity-signal-form` (`plugins/widget/animation.plugin.ts`).
 */
export const animationPluginShapeLegacy = {
    animation: animationSchemaLegacy,
} as const;

/**
 * Default animation bag. Defaults mirror the host-app factory (`fade`/`left`/`medium`,
 * inactive). Used by every zero-variant `createLegacy<W>('zero')` factory.
 */
export const createAnimationLegacy = (overrides: Partial<AnimationSettingsLegacy> = {}): AnimationSettingsLegacy => ({
    isActive: false,
    animationType: 'fade',
    animationPosition: 'left',
    animationSpeed: 'medium',
    ...overrides,
});
